import { createTheme } from "@mui/material";

declare module "@mui/material/styles" {
  interface BrandColor {
    100: PaletteColor;
    200: PaletteColor;
    300: PaletteColor;
    400: PaletteColor;
    500: PaletteColor;
    600: PaletteColor;
    700: PaletteColor;
    800: PaletteColor;
    900: PaletteColor;
  }

  interface BrandColorOptions {
    100?: SimplePaletteColorOptions;
    200?: SimplePaletteColorOptions;
    300?: SimplePaletteColorOptions;
    400?: SimplePaletteColorOptions;
    500?: SimplePaletteColorOptions;
    600?: SimplePaletteColorOptions;
    700?: SimplePaletteColorOptions;
    800?: SimplePaletteColorOptions;
    900?: SimplePaletteColorOptions;
  }

  interface Palette {
    brand: {
      black: BrandColor;
      blue: BrandColor;
      green: BrandColor;
      grey: BrandColor;
      orange: BrandColor;
      purple: BrandColor;
      red: BrandColor;
      white: BrandColor;
      yellow: BrandColor;
    };
  }

  interface PaletteOptions {
    brand?: {
      black: BrandColorOptions;
      blue: BrandColorOptions;
      green: BrandColorOptions;
      grey: BrandColorOptions;
      orange: BrandColorOptions;
      purple: BrandColorOptions;
      red: BrandColorOptions;
      white: BrandColorOptions;
      yellow: BrandColorOptions;
    };
  }

  interface PaletteColor {
    lighter?: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
  }
}

export const base = createTheme({
  breakpoints: { values: { lg: 992, md: 768, sm: 576, xl: 1200, xs: 0 } },
  palette: {
    brand: {
      black: {
        100: { main: "#373737" },
        200: { main: "#272727" },
        300: { main: "#171717" },
        400: { main: "#070707" },
        500: { main: "#000000" },
      },
      blue: {
        100: { light: "#56C3EC", lighter: "#71CDEF", main: "#3AB9E9" },
        200: { main: "#18A3D8" },
        300: {
          dark: "#106B8F",
          main: "#1380AA" /* CrowdCoursing Blue; primary #359DD8 */,
        },
        400: { main: "#0E5E7C" },
        500: { main: "#093B4E" },
      },
      green: {
        100: { main: "#D8F8AA" },
        200: { main: "#C1F17C" },
        300: { main: "#2E8B57" /* success - updated for WCAG compliance */ },
        400: { main: "#93D335" },
        500: { main: "#72B116" },
      },
      grey: {
        100: { main: "#FAFAFA" },
        200: { main: "#F5F5F5" },
        300: { main: "#EEEEEE" },
        400: { main: "#E0E0E0" },
        500: { main: "#BDBDBD" },
        600: { main: "#9E9E9E" },
        700: { main: "#757575" },
        800: { main: "#616161" },
        900: { main: "#424242" },
      },
      orange: {
        100: { light: "#FFEBD6", lighter: "#FFFAF5", main: "#FFDCB8" },
        200: { light: "#FFD2A3", main: "#FFC487" },
        300: { main: "#FFAF60" /* secondary */ },
        400: { main: "#FF9F3D" },
        500: { light: "#FF9A33", main: "#FF8C16" },
      },
      purple: {
        100: { main: "#BBC7F7" },
        200: { main: "#8A9DEC" },
        300: { main: "#6A7EDF" /* info */ },
        400: { main: "#4560CF" },
        500: { main: "#2543BD" },
      },
      red: {
        100: { main: "#FFC2B8" },
        200: { main: "#FF9787" },
        300: { main: "#FF7460" /* danger */ },
        400: { main: "#FF573D" },
        500: { main: "#FF3616" },
      },
      white: {
        100: { main: "#FFFFFF" },
        200: { main: "#FEFEFE" },
        300: { main: "#FDFDFD" },
        400: { main: "#FCFCFC" },
        500: { main: "#FBFBFB" },
      },
      yellow: {
        100: { main: "#FFEAB8" },
        200: { main: "#FFDB87" },
        300: { main: "#FFCE60" /* warn */ },
        400: { main: "#FFC53D" },
        500: { main: "#FFB916" },
      },
    },
  },
  spacing: 8,
});
